<script setup lang="ts">
	import { useCefFilterStore } from '@/stores/cefFilter'
	import { useDebounceFn } from '@vueuse/core'
	import { storeToRefs } from 'pinia'
	import { ref, toRefs, watch } from 'vue'

	import SlideOver from '@/components/SlideOver.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useChannel from '@/composables/useChannel'
	import useServices from '@/composables/useServices'

	import { State } from '@/interfaces/state'

	import useCompany from '@/modules/companies/composables/useCompany'
	import { Company } from '@/modules/companies/interfaces/company'

	import useCefFilter from '../composables/useCefFilter'
	import CefFiltersActive from './CefFiltersActive.vue'

	interface Props {
		companies: Company[]
		states: State[]
		openSlideOver: boolean
		activeTab: string
	}

	const props = defineProps<Props>()

	interface Emits {
		(e: 'update:company', companyId?: number): void
		(e: 'update:travelerName', travelerName: string): void
		(e: 'update:cef', cef: string): void
		(e: 'update:name', name: string): void
		(e: 'update:order', order: any): void
		(e: 'update:state', state: any): void
		(e: 'update:confirmation', confirmation: string): void
		(e: 'update:name', name: string): void
		(e: 'update:creatorName', creatorName: string): void
		(e: 'update:channel', channel: string): void
		(e: 'update:creationDates', creationDates: string[]): void
		(e: 'update:dates', dates: string[]): void
		(e: 'update:folio', folio: string): void
		(e: 'update:service', service: string): void
		(e: 'update:hasLetter', hasLetter: boolean | null): void
		(e: 'update:supplierName', supplierName: string): void
		(e: 'update:tripReason', tripReason: string): void
		(e: 'update:status', active: number | null): void
		(e: 'update:progressStage', progressStage: string | null): void

		(e: 'resetFilters'): void
		(e: 'search', query: string): void
		(e: 'toggleFilter', value: boolean): void
		(e: 'refetchCompanies'): void
	}

	const emits = defineEmits<Emits>()
	const { user } = useAuthUser()

	const filter_cef_company = ref(true)

	const { services } = useServices()
	const { channels, isLoading: loadingChannels } = useChannel()

	const { openSlideOver } = toRefs(props)

	const { resetFilters } = useCefFilterStore()

	const { selectedCompany, params } = storeToRefs(useCefFilterStore())

	const { activeFilters } = useCefFilter(params)

	const { company } = useCompany(
		{
			companyId: user.value?.companyId,
		},
		{
			immediate: !user.value?.internal,
		}
	)

	const formMappings = {
		progressStage: [
			{
				value: null,
				label: 'Todos',
			},
			{
				value: 'current',
				label: 'En curso',
			},
			{
				value: 'past',
				label: 'Anteriores',
			},
			{
				value: 'future',
				label: 'Próximos',
			},
		],
		dateOrderBy: [
			{
				value: 'desc',
				label: 'Más reciente',
			},
			{
				value: 'asc',
				label: 'Más antiguo',
			},
			{
				value: null,
				label: 'Todos',
			},
		],
	}

	const handleResetFilters = () => {
		selectedCompany.value =
			user.value?.internal ? undefined : props.companies[0]

		resetFilters()
		emits('resetFilters')
	}

	const handleFilterChange = (filterName: string, value: any) => {
		emits(`update:${filterName}` as any, value)
	}

	const debouncedHandleFilterChange = useDebounceFn(handleFilterChange, 500)

	const handleSearchParams = () => {
		emits('search', '')
	}

	watch(
		company,
		() => {
			selectedCompany.value = company.value
		},
		{
			immediate: true,
		}
	)

	const handleSelectedCompany = (company: Company | undefined) => {
		selectedCompany.value = company
	}
</script>

<template>
	<SlideOver
		:entity-type="''"
		:title="user?.internal ? 'Filtros de Itinerarios' : 'Filtros de viajes'"
		:when="openSlideOver"
		@update:close-slide-over="
			() => {
				emits('toggleFilter', false)
			}
		"
		@update:save-slide-over="
			() => {
				emits('toggleFilter', false)
			}
		"
		:hide-buttons="true"
	>
		<template #default>
			<div class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-full">
				<!-- Header Section Mejorado -->
				<div class="mb-8 relative">
					<!-- Decorative background -->
					<div
						class="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl opacity-30 transform -skew-y-1"
					></div>

					<div
						class="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
					>
						<div class="flex items-center gap-4 mb-3">
							<div class="p-3 bg-blue-100 rounded-lg">
								<svg
									class="w-6 h-6 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
									></path>
								</svg>
							</div>
							<div>
								<h3 class="text-xl font-bold text-gray-900">
									{{
										user?.internal ? 'Filtros Avanzados' : 'Filtros de Viajes'
									}}
								</h3>
								<p class="text-sm text-gray-600 mt-1">
									Encuentra exactamente lo que buscas con nuestros filtros
									inteligentes
								</p>
							</div>
						</div>

						<!-- Filtros Activos - Sección prominente -->
						<CefFiltersActive
							:params="params"
							:active-filters="activeFilters"
							@reset-filters="emits('resetFilters')"
							@search-params="handleSearchParams"
							:selected-company="selectedCompany"
							@handle-selected-company="handleSelectedCompany"
						/>
					</div>
				</div>

				<div class="space-y-8">
					<!-- Company Selection con diseño mejorado -->
					<div
						v-if="filter_cef_company"
						class="relative"
					>
						<div
							class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
						>
							<div class="flex items-center gap-3 mb-4">
								<div class="p-2 bg-purple-100 rounded-lg">
									<svg
										class="w-5 h-5 text-purple-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
										></path>
									</svg>
								</div>
								<h4 class="font-semibold text-gray-900">
									Selección de Empresa
								</h4>
							</div>

							<StyledCombobox
								:modelValue="selectedCompany"
								@update:model-value="
									(e) => {
										if (!e) return
										handleSelectedCompany(e)
										handleFilterChange('company', e.id)
									}
								"
								:options="props.companies"
								label="Empresa"
								placeholder="Selecciona una compañía"
								:on-input-change="
									(value) => {
										if (value) {
											emits('search', value)
										} else {
											emits('search', '')
										}
									}
								"
								class="mb-2"
								:show-input="!selectedCompany"
							>
								<template #default="{ option }">
									<span>{{ option.name }}</span>
								</template>

								<template #input-overlap>
									<template v-if="selectedCompany">
										<p class="text-sm normal-case md:text-base truncate">
											{{ `${selectedCompany.name} ` }}
										</p>

										<button
											class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
											@click="
												() => {
													selectedCompany = undefined

													if (user?.internal) {
														handleFilterChange('company', undefined)
													}
												}
											"
										>
											close
										</button>
									</template>
								</template>
							</StyledCombobox>
						</div>
					</div>

					<!-- Filtros principales organizados en secciones -->
					<div class="space-y-6">
						<!-- Sección: Identificación -->
						<div
							class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
						>
							<div
								class="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200"
							>
								<div class="flex items-center gap-3">
									<div class="p-2 bg-blue-100 rounded-lg">
										<svg
											class="w-5 h-5 text-blue-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
											></path>
										</svg>
									</div>
									<h4 class="font-semibold text-gray-900">
										Datos de Identificación
									</h4>
									<span
										class="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded-full"
									>
										Números y códigos de referencia
									</span>
								</div>
							</div>

							<div class="p-6 space-y-6">
								<!-- Primera fila: Campos principales de búsqueda -->
								<div class="grid gap-4 sm:grid-cols-2">
									<div class="space-y-2">
										<label
											for="confirmation"
											class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-blue-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
													></path>
												</svg>
												Confirmación
											</div>
										</label>
										<input
											id="confirmation"
											placeholder="Ingresa número de confirmación"
											type="text"
											name="confirmation"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
											v-model="params.confirmation"
											@input="
												debouncedHandleFilterChange(
													'confirmation',
													params.confirmation
												)
											"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Código de confirmación del itinerario
										</p>
									</div>

									<div class="space-y-2">
										<label
											for="itinerary-number"
											class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-blue-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
													></path>
												</svg>
												Número de CEF
											</div>
										</label>
										<input
											id="itinerary-number"
											v-model="params.cef"
											@input="debouncedHandleFilterChange('cef', params.cef)"
											placeholder="Ingresa número de itinerario CEF"
											type="text"
											name="itinerary-number"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Código único del itinerario
										</p>
									</div>
								</div>

								<!-- Segunda fila: Campos secundarios -->
								<div class="grid gap-4 sm:grid-cols-2">
									<div class="space-y-2 col-span-2">
										<label
											for="folio-number"
											class="block text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-blue-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
													></path>
												</svg>
												Número de Folio
											</div>
										</label>
										<input
											id="folio-number"
											v-model="params.folio"
											@input="
												debouncedHandleFilterChange('folio', params.folio)
											"
											placeholder="Ingresa número de folio"
											type="text"
											name="folio-number"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Número de referencia contable
										</p>
									</div>
								</div>
							</div>
						</div>

						<!-- Sección: Personas -->
						<div
							class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
						>
							<div
								class="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200"
							>
								<div class="flex items-center gap-3">
									<div class="p-2 bg-green-100 rounded-lg">
										<svg
											class="w-5 h-5 text-green-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
											></path>
										</svg>
									</div>
									<h4 class="font-semibold text-gray-900">
										Información de Personas
									</h4>
									<span
										class="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded-full"
									>
										Buscar por nombres y nombres de proveedores
									</span>
								</div>
							</div>

							<div class="p-6 space-y-6">
								<!-- Primera fila: Personas principales -->
								<div class="grid gap-4 sm:grid-cols-2">
									<div class="space-y-2">
										<label
											for="traveler-name"
											class="block text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-green-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
													></path>
												</svg>
												Nombre del Viajero
											</div>
										</label>
										<input
											id="traveler-name"
											v-model="params.travelerName"
											@input="
												debouncedHandleFilterChange(
													'travelerName',
													params.travelerName
												)
											"
											placeholder="Nombre y apellido del viajero"
											type="text"
											name="traveler-name"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Persona que realizará el viaje
										</p>
									</div>

									<div class="space-y-2">
										<label
											for="creator-name"
											class="block text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-green-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
													></path>
												</svg>
												Creado por
											</div>
										</label>
										<input
											id="creator-name"
											v-model="params.creatorName"
											@input="
												debouncedHandleFilterChange(
													'creatorName',
													params.creatorName
												)
											"
											placeholder="Nombre del creador del itinerario"
											type="text"
											name="creator-name"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Usuario que generó el itinerario
										</p>
									</div>
								</div>

								<!-- Segunda fila: Información del proveedor -->
								<div class="grid gap-4 sm:grid-cols-1">
									<div class="space-y-2">
										<label
											for="supplier-name"
											class="block text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors"
										>
											<div class="flex items-center gap-2">
												<svg
													class="w-4 h-4 text-green-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
													></path>
												</svg>
												Nombre del Proveedor (Hotel, aerolínea, etc.)
											</div>
										</label>
										<input
											id="supplier-name"
											v-model="params.supplierName"
											@input="
												debouncedHandleFilterChange(
													'supplierName',
													params.supplierName
												)
											"
											placeholder="Nombre de la empresa proveedora de servicios"
											type="text"
											name="supplier-name"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Nombre del hotel, aerolínea u otro proveedor
										</p>
									</div>

									<div>
										<label
											for="trip-reason-filter"
											class="block text-sm font-medium text-gray-700 mb-2"
										>
											Motivo de viaje
										</label>
										<input
											id="trip-reason-filter"
											v-model="params.tripReason"
											@input="
												debouncedHandleFilterChange(
													'tripReason',
													params.tripReason
												)
											"
											placeholder="Ej. Auditoría, Convención, Visita a cliente"
											type="text"
											name="trip-reason"
											class="border border-gray-300 rounded-lg w-full py-3 px-4 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Busca en el motivo y por categoría: «Auditoría» incluye
											todas las auditorías de la empresa
										</p>
									</div>
								</div>
							</div>
						</div>

						<!-- Sección: Configuración y Estado -->
						<div
							class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
						>
							<div
								class="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-200"
							>
								<div class="flex items-center gap-3">
									<div class="p-2 bg-purple-100 rounded-lg">
										<svg
											class="w-5 h-5 text-purple-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
											></path>
										</svg>
									</div>
									<h4 class="font-semibold text-gray-900">
										Configuración y Estatus
									</h4>
									<span
										class="text-xs text-gray-500 bg-purple-100 px-2 py-1 rounded-full"
									>
										Estatus, orden y servicios
									</span>
								</div>
							</div>

							<div class="p-6 space-y-6">
								<!-- Primera fila: Ordenamiento y Estado principal -->
								<div class="grid gap-4 sm:grid-cols-2">
									<!-- Status para tabs que no son cancelled -->
									<div
										class="space-y-1 col-span-2"
										v-if="activeTab !== 'cancelled'"
									>
										<StyledSelect
											v-model="params.status"
											@update:model-value="
												handleFilterChange('status', params.status)
											"
											label=" Estatus del viaje"
											:options="
												props.states
													.map((state) => {
														return {
															label: state.displayName,
															value: state.id,
														}
													})
													.filter((state) => state.label !== 'Cancelado')
													.concat([{ label: 'Todos los estados', value: 0 }])
											"
											placeholder="Selecciona un estado"
											class="w-full"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Estatus actual del viaje
										</p>
									</div>

									<!-- Progress Stage para tab cancelled -->
									<!-- <div
										class="space-y-1"
										v-if="activeTab !== 'cancelled'"
									>
										<StyledSelect
											v-model="params.progressStage"
											@update:model-value="
												handleFilterChange(
													'progressStage',
													params.progressStage
												)
											"
											label="Etapa de progreso"
											:options="formMappings.progressStage"
											placeholder="Selecciona una etapa"
											class="w-full"
										/>
										<p
											class="text-xs text-gray-500 mt-1 flex items-center gap-1"
										>
											<svg
												class="w-3 h-3 text-purple-600"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M13 10V3L4 14h7v7l9-11h-7z"
												></path>
											</svg>
											Etapa temporal del viaje cancelado
										</p>
									</div> -->

									<div class="space-y-1 col-span-2">
										<StyledSelect
											v-model="params.order"
											@update:model-value="
												handleFilterChange('order', params.order)
											"
											label="Ordenar por"
											:options="formMappings.dateOrderBy"
											placeholder="Selecciona orden"
											class="w-full"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Orden de la lista de resultados
										</p>
									</div>
								</div>

								<!-- Segunda fila: Servicios y canales -->
								<div class="grid gap-4 sm:grid-cols-2">
									<div class="space-y-1 col-span-2">
										<StyledSelect
											v-model="params.service"
											@update:model-value="
												handleFilterChange('service', params.service)
											"
											label="Tipo de servicio"
											:options="
												services
													.map((service) => {
														return {
															label: service.displayName,
															value: service.id,
														}
													})
													.concat([{ label: 'Todos los servicios', value: 0 }])
											"
											placeholder="Selecciona un servicio"
											class="w-full"
										/>
										<p
											class="text-xs text-gray-500 mt-1 flex items-center gap-1"
										>
											Tipo de servicio contratado
										</p>
									</div>

									<!-- <div class="space-y-1">
										<StyledSelect
											v-model="params.channel"
											@update:model-value="
												handleFilterChange('channel', params.channel)
											"
											label="Canal de venta"
											:options="
												channels
													.map((channel) => {
														return {
															label: channel.name,
															value: channel.id,
														}
													})
													.concat([{ label: 'Todos los canales', value: 0 }])
											"
											placeholder="Selecciona un canal"
											class="w-full"
											:loading-options="loadingChannels"
										/>
										<p
											class="text-xs text-gray-500 mt-1 flex items-center gap-1"
										>
											{{
												user?.internal ?
													'Canal por el cual se vendió el viaje'
												:	'Canal por el cual se reservó el viaje'
											}}
										</p>
									</div> -->
								</div>

								<!-- Tercera fila: Opciones adicionales -->
								<div class="grid gap-4 sm:grid-cols-1">
									<div class="space-y-1">
										<StyledSelect
											v-model="params.hasLetter"
											@update:model-value="
												handleFilterChange('hasLetter', params.hasLetter)
											"
											label="Carta de garantía"
											:options="[
												{ label: 'Con carta de garantía', value: 1 },
												{ label: 'Sin carta de garantía', value: 0 },
												{ label: 'Todos (con y sin carta)', value: 'Todos' },
											]"
											placeholder="Selecciona opción de carta"
											class="w-full"
										/>
										<p class="text-xs text-gray-500 mt-1">
											Presencia de carta de garantía
										</p>
									</div>
								</div>
							</div>
						</div>

						<!-- Sección: Fechas -->
						<div
							class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
						>
							<div
								class="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-gray-200"
							>
								<div class="flex items-center gap-3">
									<div class="p-2 bg-orange-100 rounded-lg">
										<svg
											class="w-5 h-5 text-orange-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											></path>
										</svg>
									</div>
									<h4 class="font-semibold text-gray-900">Filtros de Fecha</h4>
									<span
										class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full"
									>
										Selecciona rangos de fechas
									</span>
								</div>
							</div>

							<div class="p-6 grid gap-6 sm:grid-cols-2">
								<div class="space-y-2">
									<div class="flex items-center gap-2 mb-2">
										<svg
											class="w-4 h-4 text-orange-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
											></path>
										</svg>
										<span class="text-sm font-medium text-gray-700"
											>Fechas de viaje</span
										>
									</div>
									<StyledDateInput
										v-model="params.dates"
										@update:model-value="
											handleFilterChange('dates', params.dates)
										"
										label=""
										class="w-full"
										:range="true"
										placeholder="Selecciona rango de fechas de viaje"
									/>
									<p class="text-xs text-gray-500 mt-1">
										Rango de fechas en las que se realizan los viajes
									</p>
								</div>

								<div class="space-y-2">
									<div class="flex items-center gap-2 mb-2">
										<svg
											class="w-4 h-4 text-orange-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 6v6m0 0v6m0-6h6m-6 0H6"
											></path>
										</svg>
										<span class="text-sm font-medium text-gray-700"
											>Fechas de creación</span
										>
									</div>
									<StyledDateInput
										v-model="params.creationDates"
										@update:model-value="
											handleFilterChange('creationDates', params.creationDates)
										"
										label=""
										class="w-full"
										:range="true"
										placeholder="Selecciona rango de fechas de creación"
									/>
									<p class="text-xs text-gray-500 mt-1">
										Rango de fechas cuando se crearon los itinerarios
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<div
				class="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200"
			>
				<!-- Botones de acción mejorados -->
				<div class="flex items-center justify-center gap-4 p-4 w-full">
					<button
						@click.prevent="handleResetFilters"
						class="group inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
					>
						<svg
							class="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							></path>
						</svg>
						<span>Limpiar filtros</span>
					</button>
				</div>
			</div>
		</template>
	</SlideOver>
</template>

<style scoped>
	.listbox-button {
		position: relative;
		width: 100%;
		cursor: default;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		background-color: white;
		padding: 8px 40px 8px 12px;
		text-align: left;
		font-size: 14px;
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
	}

	.listbox-button:focus {
		border-color: #6366f1;
		outline: none;
		box-shadow: 0 0 0 1px #6366f1;
	}

	.listbox-options {
		position: absolute;
		z-index: 10;
		margin-top: 4px;
		max-height: 240px;
		width: 100%;
		overflow-y: auto;
		border-radius: 6px;
		background-color: white;
		padding: 4px 0;
		font-size: 14px;
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
		border: 1px solid rgba(0, 0, 0, 0.05);
	}

	.listbox-options:focus {
		outline: none;
	}

	.input {
		display: block;
		width: 100%;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		padding-right: 48px;
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
		font-size: 14px;
	}

	.input:focus {
		border-color: #6366f1;
		box-shadow: 0 0 0 1px #6366f1;
	}
</style>
